import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import SASidebar from '@/pages/sa/dashboard/components/SASidebar';
import {
  verificationRecords,
  verificationSummary,
  type VerificationRecord,
  type VerifiedEntityType,
  type VerificationStatus,
} from '@/mocks/verification';

type LoadingState = 'idle' | 'loading' | 'error' | 'ready';
type TabKey = 'radio' | 'locutor' | 'programa';

const tabs: { key: TabKey; label: string; icon: string; count: number }[] = [
  { key: 'radio', label: 'Verificado Radio', icon: 'ri-radio-line', count: 0 },
  { key: 'locutor', label: 'Verificado Locutor', icon: 'ri-user-voice-line', count: 0 },
  { key: 'programa', label: 'Verificado Programa', icon: 'ri-mic-line', count: 0 },
];

function statusConfig(s: VerificationStatus) {
  switch (s) {
    case 'verificado': return { icon: 'ri-verified-badge-line', label: 'Verificado', color: 'bg-emerald-100 text-emerald-700 border-emerald-200/60' };
    case 'pendiente': return { icon: 'ri-time-line', label: 'Pendiente', color: 'bg-amber-100 text-amber-700 border-amber-200/60' };
    case 'rechazado': return { icon: 'ri-close-circle-line', label: 'Rechazado', color: 'bg-red-100 text-red-700 border-red-200/60' };
    case 'revocado': return { icon: 'ri-arrow-go-back-line', label: 'Revocado', color: 'bg-secondary-100 text-secondary-700 border-secondary-200/60' };
  }
}

function entityIcon(type: VerifiedEntityType) {
  switch (type) {
    case 'radio': return 'ri-radio-line';
    case 'locutor': return 'ri-user-voice-line';
    case 'programa': return 'ri-mic-line';
  }
}

/* ════════════════════════════════════════════════
   KPI Banner
   ════════════════════════════════════════════════ */
function KpiBanner() {
  const s = verificationSummary;
  const items = [
    { value: s.totalVerified, label: 'Verificados', icon: 'ri-verified-badge-line', color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { value: s.totalPending, label: 'Pendientes', icon: 'ri-time-line', color: 'text-amber-600', bg: 'bg-amber-50' },
    { value: s.totalRejected, label: 'Rechazados', icon: 'ri-close-circle-line', color: 'text-red-600', bg: 'bg-red-50' },
    { value: s.totalRevoked, label: 'Revocados', icon: 'ri-arrow-go-back-line', color: 'text-secondary-600', bg: 'bg-secondary-50' },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {items.map((item) => (
        <div key={item.label} className="bg-background-50 border border-background-200 rounded-2xl p-4 flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl ${item.bg} flex items-center justify-center shrink-0`}>
            <i className={`${item.icon} ${item.color} text-lg`}></i>
          </div>
          <div className="min-w-0">
            <p className="text-foreground-950 font-extrabold text-xl leading-none">{item.value}</p>
            <p className="text-foreground-400 text-[10px] font-medium">{item.label}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ════════════════════════════════════════════════
   Tab Switcher
   ════════════════════════════════════════════════ */
function TabSwitcher({ active, onChange, records }: { active: TabKey; onChange: (t: TabKey) => void; records: VerificationRecord[] }) {
  const counts = useMemo(() => {
    const map: Record<TabKey, number> = { radio: 0, locutor: 0, programa: 0 };
    records.forEach(r => { map[r.entityType]++; });
    return map;
  }, [records]);

  return (
    <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          onClick={() => onChange(tab.key)}
          className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
            active === tab.key
              ? 'bg-background-50 text-foreground-950 shadow-sm'
              : 'text-foreground-400 hover:text-foreground-600'
          }`}
        >
          <i className={`${tab.icon} text-[13px]`}></i>
          <span>{tab.label}</span>
          <span className={`inline-flex px-1.5 py-0.5 rounded-full text-[9px] font-bold ${
            active === tab.key ? 'bg-foreground-950 text-background-50' : 'bg-background-200 text-foreground-500'
          }`}>
            {counts[tab.key]}
          </span>
        </button>
      ))}
    </div>
  );
}

/* ════════════════════════════════════════════════
   Badge Display
   ════════════════════════════════════════════════ */
function RfwBadge({ badgeNumber, size }: { badgeNumber: string; size?: 'sm' | 'md' }) {
  const isSm = size === 'sm';
  return (
    <div className={`flex items-center gap-${isSm ? '1' : '1.5'} shrink-0`}>
      <div className={`${isSm ? 'w-7 h-7' : 'w-9 h-9'} rounded-full bg-foreground-950 flex items-center justify-center`}>
        <i className={`ri-verified-badge-fill text-emerald-400 ${isSm ? 'text-sm' : 'text-base'}`}></i>
      </div>
      <div className="min-w-0">
        <p className={`text-foreground-950 ${isSm ? 'text-[10px]' : 'text-xs'} font-extrabold leading-none`}>RFW</p>
        <p className={`text-foreground-400 ${isSm ? 'text-[8px]' : 'text-[9px]'} leading-none`}>{badgeNumber}</p>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════
   Table Row
   ════════════════════════════════════════════════ */
function VerificationRow({ record, index, onClick }: { record: VerificationRecord; index: number; onClick: () => void }) {
  const sc = statusConfig(record.status);

  return (
    <div
      onClick={onClick}
      className="flex items-center gap-3 md:gap-4 p-3 md:p-4 rounded-xl hover:bg-background-100/60 transition-colors cursor-pointer group"
      style={{ animation: `fadeInUp 0.3s ease-out forwards`, animationDelay: `${index * 40}ms`, opacity: 0 }}
    >
      {/* Badge */}
      <RfwBadge badgeNumber={record.badgeNumber} size="sm" />

      {/* Entity info */}
      <div className="flex-1 min-w-0 flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl overflow-hidden shrink-0 bg-background-200">
          <img src={record.entityImageUrl} alt={record.entityName} className="w-full h-full object-cover" loading="lazy" />
        </div>
        <div className="min-w-0">
          <p className="text-foreground-950 text-xs font-semibold truncate">{record.entityName}</p>
          <p className="text-foreground-400 text-[10px] truncate">{record.entityDetail}</p>
        </div>
      </div>

      {/* Date - hidden on mobile */}
      <div className="hidden sm:flex flex-col items-end min-w-[90px] shrink-0">
        <span className="text-foreground-600 text-[10px] font-medium">{record.verificationDate}</span>
        <span className="text-foreground-400 text-[9px]">Fecha</span>
      </div>

      {/* Status */}
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[9px] font-bold border whitespace-nowrap shrink-0 ${sc.color}`}>
        <i className={`${sc.icon} text-[9px]`}></i>
        {sc.label}
      </span>

      {/* Arrow on hover */}
      <div className="hidden sm:flex w-7 h-7 rounded-lg items-center justify-center shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
        <i className="ri-arrow-right-s-line text-foreground-300 text-sm"></i>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════
   Detail Modal
   ════════════════════════════════════════════════ */
function DetailModal({ record, onClose }: { record: VerificationRecord; onClose: () => void }) {
  const sc = statusConfig(record.status);

  return (
    <>
      <div className="fixed inset-0 z-[80] bg-black/40 transition-opacity" onClick={onClose}></div>
      <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 pointer-events-none">
        <div className="bg-background-50 border border-background-200 rounded-2xl w-full max-w-md shadow-xl pointer-events-auto overflow-hidden">
          {/* Header with image */}
          <div className="relative h-36 bg-background-100 overflow-hidden">
            <img src={record.entityImageUrl} alt={record.entityName} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
            <div className="absolute bottom-0 inset-x-0 p-4">
              <div className="flex items-center gap-3">
                <RfwBadge badgeNumber={record.badgeNumber} />
                <div className="min-w-0">
                  <h3 className="text-white font-bold text-base leading-tight truncate">{record.entityName}</h3>
                  <p className="text-white/70 text-[10px] truncate">{record.entityDetail}</p>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="absolute top-3 right-3 w-7 h-7 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center cursor-pointer hover:bg-white/30 transition-colors"
            >
              <i className="ri-close-line text-white text-sm"></i>
            </button>
          </div>

          {/* Content */}
          <div className="p-5 space-y-4">
            {/* Status row */}
            <div className="flex items-center justify-between">
              <span className="text-foreground-400 text-[10px] font-medium">Estado de verificación</span>
              <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold border ${sc.color}`}>
                <i className={`${sc.icon} text-[10px]`}></i>
                {sc.label}
              </span>
            </div>

            {/* Date */}
            <div className="flex items-center justify-between">
              <span className="text-foreground-400 text-[10px] font-medium">Fecha</span>
              <span className="text-foreground-800 text-xs font-semibold">{record.verificationDate}</span>
            </div>

            {/* Type */}
            <div className="flex items-center justify-between">
              <span className="text-foreground-400 text-[10px] font-medium">Tipo</span>
              <span className="text-foreground-800 text-xs font-semibold flex items-center gap-1.5">
                <i className={`${entityIcon(record.entityType)} text-foreground-400 text-xs`}></i>
                {record.entityType === 'radio' ? 'Radio' : record.entityType === 'locutor' ? 'Locutor' : 'Programa'}
              </span>
            </div>

            {/* Reviewer */}
            <div className="flex items-center justify-between">
              <span className="text-foreground-400 text-[10px] font-medium">Revisor</span>
              <span className="text-foreground-800 text-xs font-semibold">{record.reviewer}</span>
            </div>

            {/* Divider */}
            <div className="h-px bg-background-200"></div>

            {/* Reason */}
            <div>
              <p className="text-foreground-400 text-[10px] font-medium mb-1.5">Motivo</p>
              <p className="text-foreground-700 text-[11px] leading-relaxed">{record.reason}</p>
            </div>

            {/* Notes */}
            {record.notes && (
              <div>
                <p className="text-foreground-400 text-[10px] font-medium mb-1.5">Notas</p>
                <p className="text-foreground-600 text-[11px] leading-relaxed">{record.notes}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-5 py-3 border-t border-background-200 flex items-center justify-end gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-xs font-bold text-foreground-500 hover:text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              Cerrar
            </button>
            {record.status === 'verificado' && (
              <button className="px-4 py-2 rounded-xl text-xs font-bold bg-red-50 text-red-600 hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5">
                <i className="ri-arrow-go-back-line text-[11px]"></i>
                Revocar
              </button>
            )}
            {record.status === 'pendiente' && (
              <button className="px-4 py-2 rounded-xl text-xs font-bold bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5">
                <i className="ri-check-line text-[11px]"></i>
                Aprobar
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

/* ════════════════════════════════════════════════
   Empty State
   ════════════════════════════════════════════════ */
function EmptyTab({ tab }: { tab: TabKey }) {
  const labels: Record<TabKey, string> = { radio: 'radios', locutor: 'locutores', programa: 'programas' };
  return (
    <div className="text-center py-12">
      <div className="w-14 h-14 rounded-2xl bg-background-100 flex items-center justify-center mx-auto mb-3">
        <i className="ri-inbox-line text-foreground-300 text-2xl"></i>
      </div>
      <p className="text-foreground-400 text-xs font-medium">No hay registros de verificación para {labels[tab]}</p>
    </div>
  );
}

/* ════════════════════════════════════════════════
   PAGE
   ════════════════════════════════════════════════ */
export default function SuperAdminVerification() {
  const [loadState, setLoadState] = useState<LoadingState>('loading');
  const [activeTab, setActiveTab] = useState<TabKey>('radio');
  const [selectedRecord, setSelectedRecord] = useState<VerificationRecord | null>(null);

  useEffect(() => {
    setLoadState('loading');
    const delay = 400 + Math.random() * 300;
    const shouldError = Math.random() < 0.04;
    const timer = setTimeout(() => { setLoadState(shouldError ? 'error' : 'ready'); }, delay);
    return () => clearTimeout(timer);
  }, []);

  const handleRetry = useCallback(() => {
    setLoadState('loading');
    const delay = 300 + Math.random() * 300;
    setTimeout(() => { setLoadState(Math.random() < 0.03 ? 'error' : 'ready'); }, delay);
  }, []);

  const filteredRecords = useMemo(
    () => verificationRecords.filter(r => r.entityType === activeTab),
    [activeTab],
  );

  /* ─── Loading ─── */
  if (loadState === 'loading') {
    return (
      <div className="min-h-screen bg-background-100">
        <div className="flex">
          <SASidebar />
          <main className="flex-1 min-w-0">
            <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-6 max-w-[1600px] mx-auto">
              <div className="w-40 h-5 bg-background-200 rounded-lg animate-pulse mb-1.5"></div>
              <div className="w-72 h-3.5 bg-background-200 rounded-lg animate-pulse"></div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="bg-background-50 border border-background-200 rounded-2xl p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-background-200 animate-pulse shrink-0"></div>
                    <div className="space-y-2 flex-1">
                      <div className="w-8 h-5 bg-background-200 rounded animate-pulse"></div>
                      <div className="w-16 h-3 bg-background-200 rounded animate-pulse"></div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="w-64 h-9 bg-background-200 rounded-full animate-pulse"></div>
              <div className="bg-background-50 border border-background-200 rounded-2xl p-5 space-y-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-full bg-background-200 animate-pulse shrink-0"></div>
                    <div className="w-9 h-9 rounded-xl bg-background-200 animate-pulse shrink-0"></div>
                    <div className="flex-1 space-y-2">
                      <div className="w-40 h-3.5 bg-background-200 rounded animate-pulse"></div>
                      <div className="w-56 h-2.5 bg-background-200 rounded animate-pulse"></div>
                    </div>
                    <div className="w-16 h-6 bg-background-200 rounded-lg animate-pulse shrink-0"></div>
                  </div>
                ))}
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  /* ─── Error ─── */
  if (loadState === 'error') {
    return (
      <div className="min-h-screen bg-background-100">
        <div className="flex">
          <SASidebar />
          <main className="flex-1 min-w-0 flex items-center justify-center p-4">
            <div className="bg-background-50 border border-background-200 rounded-2xl p-8 max-w-sm w-full text-center">
              <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4">
                <i className="ri-error-warning-line text-red-500 text-2xl"></i>
              </div>
              <h2 className="text-foreground-950 font-bold text-base mb-1.5">Error cargando Verificación RFW</h2>
              <p className="text-foreground-400 text-xs mb-5">No se pudieron cargar los registros de verificación. Verificá tu conexión e intentá de nuevo.</p>
              <button
                onClick={handleRetry}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-foreground-950 text-background-50 text-sm font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line text-sm"></i>
                Reintentar
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  /* ─── Ready ─── */
  return (
    <div className="min-h-screen bg-background-100">
      <div className="flex">
        <SASidebar />
        <main className="flex-1 min-w-0">
          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5 max-w-[1600px] mx-auto">

            {/* Breadcrumbs */}
            <div className="flex items-center gap-1.5 text-[10px] text-foreground-400">
              <Link to="/sa/dashboard" className="hover:text-foreground-600 transition-colors cursor-pointer">Dashboard</Link>
              <i className="ri-arrow-right-s-line text-[9px]"></i>
              <span className="text-foreground-500 font-medium">Verificación RFW</span>
            </div>

            {/* Page Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-foreground-950 font-bold text-base">Verificación RFW</h2>
                <p className="text-foreground-400 text-xs mt-0.5">
                  Sistema de insignias oficiales — Radios, Locutores y Programas verificados · {verificationSummary.totalVerified} verificados activos
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="px-2.5 py-1 rounded-full bg-background-200/70 text-foreground-500 text-[10px] font-medium whitespace-nowrap flex items-center gap-1">
                  <i className="ri-calendar-check-line text-[9px]"></i>
                  Última: {verificationSummary.lastVerificationDate}
                </span>
              </div>
            </div>

            {/* Concept Banner */}
            <div className="bg-background-50 border border-background-200 rounded-2xl p-4 md:p-5">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-foreground-950 flex items-center justify-center shrink-0">
                    <i className="ri-verified-badge-fill text-emerald-400 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-foreground-800 text-xs font-semibold">Insignia Oficial RFW</p>
                    <p className="text-foreground-500 text-[10px]">
                      La insignia RFW certifica que una radio, locutor o programa cumple con los estándares de calidad, autenticidad y compromiso comunitario del ecosistema RadioFrecuenciaWeb. Cada verificación es auditada por Super Admin y puede ser revocada si se incumplen los criterios.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* KPIs */}
            <KpiBanner />

            {/* Tabs */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <TabSwitcher active={activeTab} onChange={setActiveTab} records={verificationRecords} />
              <span className="text-foreground-400 text-[10px] sm:ml-auto">
                {filteredRecords.length} registro{filteredRecords.length !== 1 ? 's' : ''}
              </span>
            </div>

            {/* Table */}
            <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
              {/* Table header */}
              <div className="hidden md:flex items-center gap-4 px-4 py-2.5 border-b border-background-200 bg-background-100/40">
                <div className="w-[76px] shrink-0">
                  <span className="text-foreground-400 text-[9px] font-bold uppercase tracking-wider">Insignia</span>
                </div>
                <div className="flex-1">
                  <span className="text-foreground-400 text-[9px] font-bold uppercase tracking-wider">Entidad</span>
                </div>
                <div className="w-[90px] shrink-0 text-right">
                  <span className="text-foreground-400 text-[9px] font-bold uppercase tracking-wider">Fecha</span>
                </div>
                <div className="w-[100px] shrink-0">
                  <span className="text-foreground-400 text-[9px] font-bold uppercase tracking-wider">Estado</span>
                </div>
              </div>

              {/* Rows */}
              <div className="divide-y divide-background-200 px-1">
                {filteredRecords.length === 0 ? (
                  <EmptyTab tab={activeTab} />
                ) : (
                  filteredRecords.map((record, idx) => (
                    <VerificationRow
                      key={record.id}
                      record={record}
                      index={idx}
                      onClick={() => setSelectedRecord(record)}
                    />
                  ))
                )}
              </div>
            </div>

            {/* Breakdown by type */}
            <div className="bg-background-50 border border-background-200 rounded-2xl p-4 md:p-5">
              <h3 className="text-foreground-950 font-semibold text-sm mb-3">Resumen por Tipo</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { label: 'Radios', icon: 'ri-radio-line', verified: verificationSummary.verifiedRadios, pending: verificationSummary.pendingRadios, color: 'emerald' },
                  { label: 'Locutores', icon: 'ri-user-voice-line', verified: verificationSummary.verifiedBroadcasters, pending: verificationSummary.pendingBroadcasters, color: 'accent' },
                  { label: 'Programas', icon: 'ri-mic-line', verified: verificationSummary.verifiedPrograms, pending: verificationSummary.pendingPrograms, color: 'primary' },
                ].map((item) => (
                  <div key={item.label} className="bg-background-100 rounded-xl p-4 flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-xl bg-${item.color}-100 flex items-center justify-center shrink-0`}>
                      <i className={`${item.icon} text-${item.color}-600 text-base`}></i>
                    </div>
                    <div className="min-w-0 space-y-1">
                      <p className="text-foreground-800 text-xs font-semibold">{item.label}</p>
                      <div className="flex items-center gap-3">
                        <span className="text-emerald-600 text-[10px] font-bold flex items-center gap-1">
                          <i className="ri-verified-badge-line text-[9px]"></i>
                          {item.verified}
                        </span>
                        {item.pending > 0 && (
                          <span className="text-amber-600 text-[10px] font-bold flex items-center gap-1">
                            <i className="ri-time-line text-[9px]"></i>
                            {item.pending}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </main>
      </div>

      {/* Detail Modal */}
      {selectedRecord && (
        <DetailModal record={selectedRecord} onClose={() => setSelectedRecord(null)} />
      )}

      {/* Mobile bottom bar */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-background-50/98 backdrop-blur-2xl border-t border-background-200 py-2.5 px-4 safe-bottom">
        <p className="text-center text-foreground-400 text-[10px] font-medium">
          Super Admin · Verificación RFW
        </p>
      </div>
    </div>
  );
}